import { useState } from "react";
import { useClerk, useUser } from "@clerk/clerk-react";
import styles from "./UserMenu.module.scss";

export type AppSurface = "cockpit" | "operator" | "facilitator";

interface SurfaceDescriptor {
  surface: AppSurface;
  label: string;
  href: string;
}

const SURFACES: SurfaceDescriptor[] = [
  { surface: "cockpit", label: "Organizer cockpit", href: "/clubs" },
  { surface: "operator", label: "Operator console", href: "/staff" },
  {
    surface: "facilitator",
    label: "Facilitator dashboard",
    href: "/facilitate",
  },
];

interface UserMenuProps {
  currentSurface: AppSurface;
  accessibleSurfaces: readonly AppSurface[];
  placement: "sidebar" | "topnav";
  defaultOpen?: boolean;
}

export function UserMenu({
  currentSurface,
  accessibleSurfaces,
  placement,
  defaultOpen = false,
}: UserMenuProps) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(defaultOpen);

  if (!user) return null;

  const otherSurfaces = SURFACES.filter(
    (s) =>
      accessibleSurfaces.includes(s.surface) && s.surface !== currentSurface,
  );

  const displayName =
    user.fullName ?? user.username ?? user.firstName ?? "Signed in";
  const email = user.primaryEmailAddress?.emailAddress ?? "";
  const avatarLetter = (user.firstName ?? user.username ?? displayName ?? "U")
    .slice(0, 1)
    .toUpperCase();

  const handleSignOut = () => {
    void signOut({ redirectUrl: "/sign-in" });
  };

  const rootClass = `${styles.root} ${styles[`placement_${placement}`]} ${
    open ? styles.rootOpen : ""
  }`;

  return (
    <div className={rootClass}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={displayName}
      >
        <span className={styles.avatar} aria-hidden>
          {user.imageUrl ? (
            <img className={styles.avatarImage} src={user.imageUrl} alt="" />
          ) : (
            avatarLetter
          )}
        </span>
        <span className={styles.identity}>
          <span className={styles.name}>{displayName}</span>
          <span className={styles.email}>{email}</span>
        </span>
        <span className={styles.chev} aria-hidden>
          {open ? "▾" : "▴"}
        </span>
      </button>

      {open && (
        <div className={styles.expand}>
          {otherSurfaces.length > 0 && (
            <>
              <p className={styles.expandLabel}>Switch to</p>
              <ul className={styles.expandList}>
                {otherSurfaces.map((s) => (
                  <li key={s.surface}>
                    <a href={s.href} className={styles.expandLink}>
                      <span>{s.label}</span>
                      <span className={styles.expandArrow} aria-hidden>
                        →
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
              <hr className={styles.divider} />
            </>
          )}
          <button
            type="button"
            className={styles.signOut}
            onClick={handleSignOut}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
